import {Autocomplete, Button, TextField, useTheme} from "@mui/material/index";
import { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { GlobalContext } from "../../../../../util/state/GlobalContextProvider";
import ExpiredCustomDialog from "../../../common/dialog/ExpiredCustomDialog";
import { Controller, useForm, useFormContext } from "react-hook-form";
import { IExpiredPracticeDetailForm, IExpiredPracticeSaveRecoveryCompanyParams, IRecoveryCompanyForm, RecoveryCompany } from "../../../models/models";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { findRecoveryCompany, saveRecoveryCompany } from "../../../services/http.services";
import { EXPIRED_PRACTICES_QUERY_KEY, EXPIRED_PRACTICE_RECOVERY_COMPANY_FORM_VALIDATION_SCHEMA, EXPIRED_RECOVERY_COMPANY_FORM_INITIAL_VALUE } from "../../../config";
import { yupResolver } from "@hookform/resolvers/yup";
import { snackbarTypes } from "../../../../../util/state/globalVariables";
import { useRecoilState, useSetRecoilState } from "recoil";
import { practiceDetail, recoveryCompanyOptionsState } from "../../../state";
import CustomLoader from "../../../../../util/component/CustomLoader";
import { setSnackbar } from "../../../../../util/state/globalAction";

const RecoveryCompanyActionButton = () => {

    const theme = useTheme();
    const {t} = useTranslation(['expiredManagement']);
    const { dispatchGlobalData } = useContext(GlobalContext);

    const setPracticeDetail = useSetRecoilState(practiceDetail);

    const expiredPracticeDetailForm = useFormContext<IExpiredPracticeDetailForm>();

    const recoveryCompanySelectedForm = useForm<IRecoveryCompanyForm>({
        defaultValues: EXPIRED_RECOVERY_COMPANY_FORM_INITIAL_VALUE,
        resolver: yupResolver(EXPIRED_PRACTICE_RECOVERY_COMPANY_FORM_VALIDATION_SCHEMA)
    });

    const [recoveryCompanyOptions, setRecoveryCompanyOptions] = useRecoilState(recoveryCompanyOptionsState);

    const [openDialog, setOpenDialog] = useState(false);

    const queryClient = useQueryClient();

    const handleRecoveryCompany = (event: React.MouseEvent) => {
        event.stopPropagation();

        setOpenDialog(true);
    }

    useEffect(() => {
        if(recoveryCompanyOptions.length == 0){
            const recoveryCompaniesData = queryClient.getQueryData<RecoveryCompany[]>([EXPIRED_PRACTICES_QUERY_KEY.FIND_RECOVERY_COMPANIES]);
            
            if(!!recoveryCompaniesData && recoveryCompanyOptions.length > 0)
                setRecoveryCompanyOptions(recoveryCompaniesData);
            else{
                findRecoveryCompaniesQuery.refetch();
            }
        }

    }, []);

    const findRecoveryCompaniesQuery = useQuery(
        [EXPIRED_PRACTICES_QUERY_KEY.FIND_RECOVERY_COMPANIES],
        findRecoveryCompany, 
        {
            enabled: false,
            onSuccess(response) {
                setRecoveryCompanyOptions(response?.data || [])
            }
        }
    );

    const saveRecoveryCompanyMutation = useMutation({
        mutationFn: (params: IExpiredPracticeSaveRecoveryCompanyParams) => {
            return saveRecoveryCompany(params);
        },
        onSuccess: (response) => {
            
            dispatchGlobalData({
                type: setSnackbar,
                payload: {
                    snackbar: {
                        message: t('common.message.success'),
                        type: snackbarTypes.success,
                        show: true,
                        autoClose: true,
                    }
                }
            });

            setPracticeDetail(response.data);

            setOpenDialog(false);
        },
        onError: (error: any) => {

            dispatchGlobalData({
                type: setSnackbar,
                payload: {
                    snackbar: {
                        message: t(`common.message.error.saveExpiredRecoveryCompany.${error?.response?.status}`, t('common.message.error.tryLater')),
                        type: snackbarTypes.error,
                        show: true,
                        autoClose: true,
                    }
                }
            });
            
        },
    });

    const onConfirm = (formData: IRecoveryCompanyForm) => {
        const expiredcode = expiredPracticeDetailForm.getValues("detail.expiredCode");

        if(!!expiredcode ) {
            const req: IExpiredPracticeSaveRecoveryCompanyParams = {
                id: expiredcode,
                recoveryCompany: {
                    amount:  formData.recoveryCompany?.amount || '',
                    email: formData.recoveryCompany?.email || '',
                    name: formData.recoveryCompany?.name || '',
                    tenant: formData.recoveryCompany?.tenant || ''
                }
            }
            saveRecoveryCompanyMutation.mutate(req);
        }
    }

    const onClose = () => {
        setOpenDialog(false);
        recoveryCompanySelectedForm.reset();
    }

    return (
        <>
            <Button sx={{ml: theme.spacing(1)}} onClick={handleRecoveryCompany}>
                {t('recoveryCompany.button.recoveryCompany')}
            </Button>

            <ExpiredCustomDialog 
                open={openDialog} 
                title={t('recoveryCompany.dialog.title')}
                contentDescription={
                    <Controller
                        control={recoveryCompanySelectedForm.control}
                        name={'recoveryCompany'}
                        render={({field, fieldState}) => 
                            <Autocomplete
                                autoHighlight
                                autoSelect
                                fullWidth
                                options={recoveryCompanyOptions}
                                loading={findRecoveryCompaniesQuery.isLoading}
                                renderInput={(params ) => 
                                    <TextField
                                        {...params} 
                                        label={t('recoveryCompany.form.recoveryCompany')}
                                        required
                                        fullWidth
                                        error={!!fieldState.error}
                                        helperText={t(!!fieldState.error?.message ? fieldState.error.message : '')}
                                    />
                                }
                                isOptionEqualToValue={(option: any, value: any) => option?.name === value?.name}
                                getOptionLabel={(option: any) => option?.name}
                                onChange={(_e, value) => {
                                    field.onChange(value);
                                }}               
                            />
                        }
                    /> 
                }
                buttonCloseDescription={t('common.button.cancel')}
                buttonConfirmDescription={t('common.button.confirm')}
                onClose={onClose}
                onConfirm={recoveryCompanySelectedForm.handleSubmit(onConfirm)}           
            />

            <CustomLoader loading={saveRecoveryCompanyMutation.isLoading} />
        </>
    )

}

export default RecoveryCompanyActionButton;